import { errorResponseDto } from '@planuze/admin-contracts';
import { z } from 'zod';

/* Núcleo HTTP compartilhado do cliente REST tipado do backoffice (ADR-0354/0699).
   Cada domínio (auth, companies, …) reusa `request`/`parseJson`/`mapHttpError` e os
   helpers (`HTTP`, `buildQuery`, `cursorPageSchema`). Cookie HttpOnly é o token →
   `credentials:'include'` em toda chamada (§4.9). */

export type ApiErrorCode =
  | 'invalid_credentials'
  | 'rate_limited'
  | 'account_locked'
  | 'unauthorized'
  | 'not_found'
  | 'not_blocked'
  | 'owner_not_found'
  | 'plan_key_taken'
  | 'invalid'
  | 'invalid_format'
  | 'public_blocked'
  | 'invalid_slug'
  | 'slug_taken'
  | 'invalid_logo'
  | 'logo_too_large'
  | 'already_publisher'
  | 'conflict'
  | 'email_taken'
  | 'last_super_admin'
  | 'cannot_suspend_self'
  | 'grace_expired'
  | 'invalid_transition'
  | 'invalid_response'
  | 'server_error'
  | 'network'
  | 'unknown';

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  /** Código bruto emitido pelo backend (`{ error }`) — preserva a causa específica
      quando o `code` acima colapsa em genérico. Usado pelos error-mappers pt-BR. */
  readonly serverCode: string;

  constructor(status: number, code: ApiErrorCode, message: string, serverCode = '') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.serverCode = serverCode;
  }
}

/* Fonte única de status HTTP (antes redeclarados em ~7 clientes). Códigos HTTP são
   exceção legítima ao no-magic-numbers (§4.11/§4.17). */
export const HTTP = {
  ok: 200,
  badRequest: 400,
  unauthorized: 401,
  forbidden: 403,
  notFound: 404,
  conflict: 409,
  payloadTooLarge: 413,
  tooMany: 429,
  serverError: 500,
  badGateway: 502,
} as const;

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
}

export async function request(path: string, options: RequestOptions = {}): Promise<Response> {
  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  };
  if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }
  try {
    return await fetch(path, init);
  } catch {
    throw new ApiError(0, 'network', 'network request failed');
  }
}

export async function parseJson<T>(res: Response, schema: z.ZodType<T>): Promise<T> {
  const raw: unknown = await res.json().catch(() => null);
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ApiError(res.status, 'invalid_response', 'unexpected response shape');
  }
  return parsed.data;
}

/** Lê o `{ error: code }` do corpo de erro (validado pela forma canônica). String vazia
    quando o corpo não segue o contrato. */
export async function readErrorCode(res: Response): Promise<string> {
  const raw: unknown = await res.json().catch(() => null);
  const parsed = errorResponseDto.safeParse(raw);
  return parsed.success ? parsed.data.error : '';
}

/** Query string a partir de um mapa esparso (ignora `undefined`/vazio). Substitui os
    `buildQuery` copiados em 4 clientes. */
export function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    const str = String(value);
    if (str.length === 0) continue;
    search.set(key, str);
  }
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

/** Schema de página cursor-based `{ items, nextCursor }` (antes reescrito em 3 clientes). */
export function cursorPageSchema<T>(itemSchema: z.ZodType<T>) {
  return z.object({ items: z.array(itemSchema), nextCursor: z.string().nullish() });
}

/** Mapa status→ApiErrorCode por domínio; o default cobre 401/403/network/conflito. */
type StatusCodeMap = Partial<Record<number, ApiErrorCode>>;

/** Fábrica de erro tipado: aplica o mapa do domínio sobre o status + preserva o código
    bruto do backend em `serverCode`. 401/403 têm tratamento uniforme aqui (o global
    onError do QueryClient cuida do redirect em 401). */
export async function mapHttpError(
  res: Response,
  overrides: StatusCodeMap = {},
): Promise<ApiError> {
  const serverCode = await readErrorCode(res);
  const mapped = overrides[res.status];
  if (mapped !== undefined)
    return new ApiError(res.status, mapped, serverCode || mapped, serverCode);
  switch (res.status) {
    case HTTP.unauthorized:
    case HTTP.forbidden:
      return new ApiError(res.status, 'unauthorized', serverCode || 'unauthorized', serverCode);
    case HTTP.notFound:
      return new ApiError(res.status, 'not_found', serverCode || 'not found', serverCode);
    case HTTP.conflict:
      return new ApiError(res.status, 'conflict', serverCode || 'conflict', serverCode);
    default:
      return new ApiError(res.status, 'server_error', serverCode || 'request failed', serverCode);
  }
}
